import { FC, MutableRefObject, useEffect } from 'react';

import { CostChip, TokensChip } from '@/Pages/Run/components/TokensChip';
import { FeedbackChips } from '@/components/FeedbackChips';
import { RunLatencyChip } from '@/components/RunLatencyChip';
import { RunStatusChip } from '@/components/RunStatusChip';
import {
  DatasetSchema,
  ExampleSchemaWithRunsAndOptionalFields,
  RunSchema,
} from '@/types/schema';

import {
  COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW,
  EXPERIMENT_OUTPUT_WIDTH,
  OUTPUT_CELL_PRE_RUN_PLACEHOLDER_TEXT,
} from '../constants';
import { ExperimentLanguage } from '../types';
import { TExampleDiff } from '../types';
import { isInPlayground } from '../utils/isInPlayground';
import { TGetRunDiffArgs } from '../utils/useGetExperimentDiffs';
import { DatasetSessionCompareRunActionButtons } from './DatasetSessionCompareRunActionButtons';
import { DatasetSessionCompareSidePaneDetailS3Outputs } from './DatasetSessionCompareSidePaneDetailOutputs';

type TProps = {
  run: RunSchema;
  example: ExampleSchemaWithRunsAndOptionalFields;
  repetitionNumber: number;
  sortedRunIds: string[];
  idx: number;
  dataset?: DatasetSchema;
  datasetShareToken: string | undefined;
  language?: ExperimentLanguage;
  setDetailRunId: (runId: string | null) => void;
  setAnnotationDetailRunId: (runId: string | null) => void;
  mutateInfiniteExamples: () => void;
  itemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
  getRunDiffs: (args: TGetRunDiffArgs) => void;
  diffs: TExampleDiff | undefined;
};

const DatasetSessionCompareRepetition: FC<TProps> = ({
  run,
  example,
  repetitionNumber,
  sortedRunIds,
  idx,
  dataset,
  datasetShareToken,
  language,
  setDetailRunId,
  setAnnotationDetailRunId,
  mutateInfiniteExamples,
  itemRefs,
  getRunDiffs,
  diffs,
}) => {
  const inPlayground = isInPlayground();
  const feedbackStats = { ...run?.feedback_stats };
  useEffect(() => {
    getRunDiffs({
      exampleId: example.id,
      runOutputs: run?.outputs,
      runOutputsPreview: run?.outputs_preview,
      exampleOutputs: example.outputs,
      runId: run?.id,
    });
  }, [run, getRunDiffs, example.id, example.outputs]);
  return (
    <div
      key={run.id}
      className="flex flex-col gap-3 rounded-xl border border-secondary p-4"
      style={{
        width:
          sortedRunIds.length === 1
            ? '100%'
            : sortedRunIds.length === 2
            ? '50%'
            : sortedRunIds.length === 3
            ? '33.33%'
            : sortedRunIds.length === 4
            ? '25%'
            : sortedRunIds.length === 5
            ? '20%'
            : EXPERIMENT_OUTPUT_WIDTH,
        minWidth: EXPERIMENT_OUTPUT_WIDTH,
      }}
      ref={(el) => {
        itemRefs.current[idx] = el;
      }}
    >
      <div className="flex items-center justify-between gap-6">
        <h2 className="line-clamp-1 whitespace-normal break-words break-all text-lg font-semibold">
          Repetition {repetitionNumber}
        </h2>
        {run != null && (
          <DatasetSessionCompareRunActionButtons
            run={run}
            datasetShareToken={datasetShareToken}
            setDetailRunId={setDetailRunId}
            setAnnotationDetailRunId={setAnnotationDetailRunId}
          />
        )}
      </div>
      {run ? (
        <div className="flex flex-1 flex-col justify-between gap-3 overflow-y-auto">
          <DatasetSessionCompareSidePaneDetailS3Outputs
            run={run}
            inputs={example?.inputs}
            outputs={run?.outputs}
            error={run?.error}
            dataType={dataset?.data_type ?? 'kv'}
            language={language}
            diffs={diffs?.runs[run.id]?.runOutput}
          />

          <div className="flex flex-col gap-2">
            <div className="flex flex-shrink flex-wrap items-start gap-2">
              <FeedbackChips
                feedbackStats={feedbackStats}
                feedbackSourceRunId={run.id}
                allowTruncation
                disablePopover={!!datasetShareToken}
                onMutate={mutateInfiniteExamples}
                maxNumberOfCategories={
                  COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW
                }
              />
            </div>

            <div className="flex flex-shrink flex-wrap items-start gap-2">
              <RunLatencyChip run={run} />

              <RunStatusChip run={run} className="px-1.5" size="xs" />

              <TokensChip run={run} />
              <CostChip run={run} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-start justify-start p-4">
          <p className="text-sm italic text-quaternary">
            {inPlayground
              ? OUTPUT_CELL_PRE_RUN_PLACEHOLDER_TEXT
              : 'Experiment has no run for this example'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DatasetSessionCompareRepetition;
